import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import JobSourceVM from "../types/jobSource";
import SearchResults from "../types/searchResults";
import SearchParams, { defaultParams } from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";

interface JobSourceSliceModel {
  jobSource: JobSourceVM;
  jobSourceFilter: SearchParams;
  jobSourceSearchResults?: SearchResults;
  status: "idle" | "loading" | "failed";
}

const initialState: JobSourceSliceModel = {
  jobSource: { id: 0, name: "" },
  jobSourceFilter: defaultParams,
  status: "idle",
};

export const getJobSourceId = createAsyncThunk<JobSourceVM, number>(
  "job-source/get/id",
  async (id: number) => {
    const response = await http.get<JobSourceVM>(`/jobsource/${id}`);
    return response.data;
  }
);

export const editJobSource = createAsyncThunk<JobSourceVM, JobSourceVM>(
  "job-source/edit",
  async (source: JobSourceVM) => {
    const response = await http.put<JobSourceVM>(
      `/jobsource/${source.id}`,
      source,
      {
        params: {
          successToast: true,
        },
      }
    );
    return response.data;
  }
);

export const deleteJobSource = createAsyncThunk<JobSourceVM, number>(
  "job-source/delete",
  async (id: number) => {
    const response = await http.delete<JobSourceVM>(`/jobsource/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const postJobSource = createAsyncThunk<JobSourceVM, JobSourceVM>(
  "job-source/post",
  async (source: JobSourceVM) => {
    const response = await http.post<JobSourceVM>(`/jobsource`, source, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const searchJobSource = createAsyncThunk<SearchResults, SearchParams>(
  "job-source/search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(`/jobsource/search?${qs}`);
    return response.data;
  }
);

const jobSourceSlice = createSlice({
  name: "jobSource",
  initialState,
  reducers: {
    resetJobSource(state) {
      state.jobSource = { ...initialState.jobSource };
    },
    setJobSourceFilter(state, action) {
      state.jobSourceFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getJobSourceId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getJobSourceId.fulfilled, (state, action) => {
        state.status = "idle";
        state.jobSource = action.payload;
      })
      .addCase(getJobSourceId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editJobSource.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editJobSource.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(editJobSource.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteJobSource.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteJobSource.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteJobSource.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postJobSource.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postJobSource.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(postJobSource.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchJobSource.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchJobSource.fulfilled, (state, action) => {
        state.status = "idle";
        state.jobSourceSearchResults = action.payload;
      })
      .addCase(searchJobSource.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default jobSourceSlice.reducer;
export const { resetJobSource, setJobSourceFilter } = jobSourceSlice.actions;
